import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAdminMode } from '../context/AdminModeContext';
import PortfolioLMS from '../components/PortfolioLMS';
import {
  getTrainingModules,
  createTrainingModule,
  updateTrainingModule,
  deleteTrainingModule,
  getTrainingAssignments,
  createTrainingAssignment,
  getTrainingProgress,
  getMyTrainingProgress,
  startTraining,
  updateTrainingProgress,
  getLearningRecommendations,
  getLMSStats,
  getOrganizations,
  uploadTrainingVideo,
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import {
  BookOpen,
  GraduationCap,
  Video,
  Play,
  CheckCircle,
  Clock,
  AlertTriangle,
  Plus,
  Edit,
  Trash2,
  Users,
  Building2,
  Target,
  Award,
  TrendingUp,
  Eye,
  Send,
  Upload,
  Loader2,
  ExternalLink,
} from 'lucide-react';

// Training categories
const TRAINING_CATEGORIES = [
  'Safety Training',
  'Compliance',
  'Professional Development',
  'Emergency Response',
  'Risk Management',
  'Policy Implementation',
  'Leadership',
  'Technical Skills',
];

export default function LMS() {
  const { user, isSuperAdmin, isOrgAdmin } = useAuth();
  const { isPlatformMode, isOrganizationMode, selectedOrg, organizations: contextOrgs } = useAdminMode();
  
  // Platform Management State
  const [trainingModules, setTrainingModules] = useState([]);
  const [lmsStats, setLmsStats] = useState(null);
  
  // Dialogs
  const [showModuleDialog, setShowModuleDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [editingModule, setEditingModule] = useState(null);
  
  // Organization View State
  const [myProgress, setMyProgress] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [orgTrainingAssignments, setOrgTrainingAssignments] = useState([]);
  
  // Form State
  const [moduleForm, setModuleForm] = useState({
    title: '',
    description: '',
    content_type: 'video',
    category: '',
    content_url: '',
    content_text: '',
    duration_minutes: 0,
    passing_score: 80,
    tags: [],
    applicability: { org_types: [], levels: [] },
  });
  
  // Assignment form
  const [assignForm, setAssignForm] = useState({
    organization_id: '',
    due_date: '',
  });
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Video upload state
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  
  // Orgs for assignment
  const [organizations, setOrganizations] = useState([]);
  
  // Active training state
  const [activeTraining, setActiveTraining] = useState(null);
  const [trainingProgress, setTrainingProgress] = useState(0);

  // Fetch platform data (for super admins)
  const fetchPlatformData = useCallback(async () => {
    if (!isSuperAdmin()) return;
    
    try {
      setLoading(true);
      const [modulesRes, statsRes, orgsRes] = await Promise.all([
        getTrainingModules(),
        getLMSStats(),
        getOrganizations(),
      ]);
      
      setTrainingModules(modulesRes.data || []);
      setLmsStats(statsRes.data);
      setOrganizations(orgsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch platform data:', error);
      toast.error('Failed to load training data');
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  // Fetch organization data (for org view)
  const fetchOrgData = useCallback(async () => {
    if (!selectedOrg) return;
    
    try {
      setLoading(true);
      const [progressRes, recommendationsRes, assignmentsRes] = await Promise.all([
        getMyTrainingProgress(),
        getLearningRecommendations(selectedOrg.id),
        getTrainingAssignments({ organization_id: selectedOrg.id }),
      ]);
      
      setMyProgress(progressRes.data || []);
      setRecommendations(recommendationsRes.data || []);
      setOrgTrainingAssignments(assignmentsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch org data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg]);

  useEffect(() => {
    if (isPlatformMode && isSuperAdmin()) {
      fetchPlatformData();
    } else if (isOrganizationMode && selectedOrg) {
      fetchOrgData();
    }
  }, [isPlatformMode, isOrganizationMode, selectedOrg, fetchPlatformData, fetchOrgData, isSuperAdmin]);

  // Create Training Module
  const handleCreateModule = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createTrainingModule(moduleForm);
      toast.success('Training module created successfully');
      setShowModuleDialog(false);
      resetModuleForm();
      fetchPlatformData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create training module');
    } finally {
      setSubmitting(false);
    }
  };

  // Edit Training Module
  const handleEditModule = (module) => {
    setEditingModule(module);
    setModuleForm({
      title: module.title || '',
      description: module.description || '',
      content_type: module.content_type || 'video',
      category: module.category || '',
      content_url: module.content_url || '',
      content_text: module.content_text || '',
      duration_minutes: module.duration_minutes || 0,
      passing_score: module.passing_score || 80,
      tags: module.tags || [],
      applicability: module.applicability || { org_types: [], levels: [] },
    });
    setShowModuleDialog(true);
  };

  // Update Training Module
  const handleUpdateModule = async (e) => {
    e.preventDefault();
    if (!editingModule) return;
    
    setSubmitting(true);
    try {
      await updateTrainingModule(editingModule.id, moduleForm);
      toast.success('Training module updated successfully');
      setShowModuleDialog(false);
      setEditingModule(null);
      resetModuleForm();
      fetchPlatformData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update training module');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete Training Module
  const handleDeleteModule = async (moduleId) => {
    if (!confirm('Are you sure you want to delete this training module?')) return;
    
    try {
      await deleteTrainingModule(moduleId);
      toast.success('Training module deleted');
      fetchPlatformData();
    } catch (error) {
      toast.error('Failed to delete training module');
    }
  };

  // Publish Training Module
  const handlePublishModule = async (moduleId) => {
    try {
      await updateTrainingModule(moduleId, { status: 'active' });
      toast.success('Training module published');
      fetchPlatformData();
    } catch (error) {
      toast.error('Failed to publish training module');
    }
  };

  // Reset form
  const resetModuleForm = () => {
    setModuleForm({
      title: '',
      description: '',
      content_type: 'video',
      category: '',
      content_url: '',
      content_text: '',
      duration_minutes: 0,
      passing_score: 80,
      tags: [],
      applicability: { org_types: [], levels: [] },
    });
    setVideoFile(null);
  };

  // Handle dialog close
  const handleModuleDialogClose = (open) => {
    if (!open) {
      setEditingModule(null);
      resetModuleForm();
    }
    setShowModuleDialog(open);
  };

  // Video upload handler
  const handleVideoUpload = async () => {
    if (!videoFile) return;
    
    setUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append('file', videoFile);
      
      const response = await uploadTrainingVideo(formData);
      setModuleForm(prev => ({ ...prev, content_url: response.data.url }));
      toast.success('Video uploaded successfully');
      setVideoFile(null);
    } catch (error) {
      toast.error('Failed to upload video');
    } finally {
      setUploadingVideo(false);
    }
  };

  // Assign training to org
  const handleAssignTraining = async (e) => {
    e.preventDefault();
    if (!selectedModule || !assignForm.organization_id) return;
    
    setSubmitting(true);
    try {
      await createTrainingAssignment({
        training_module_id: selectedModule.id,
        organization_id: assignForm.organization_id,
        due_date: assignForm.due_date || null,
      });
      toast.success('Training assigned successfully');
      setShowAssignDialog(false);
      setSelectedModule(null);
      setAssignForm({ organization_id: '', due_date: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign training');
    } finally {
      setSubmitting(false);
    }
  };

  // Start training
  const handleStartTraining = async (module) => {
    try {
      await startTraining(module.id);
      setActiveTraining(module);
      setTrainingProgress(0);
    } catch (error) {
      toast.error('Failed to start training');
    }
  };

  // Complete training
  const handleCompleteTraining = async () => {
    if (!activeTraining) return;
    
    try {
      await updateTrainingProgress(activeTraining.id, {
        status: 'completed',
        progress_percentage: 100,
        score: 100,
      });
      toast.success('Training completed!');
      setActiveTraining(null);
      fetchOrgData();
    } catch (error) {
      toast.error('Failed to complete training');
    }
  };

  // Extract YouTube video ID
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  // Show portfolio view for super admin in platform mode
  if (isPlatformMode && isSuperAdmin()) {
    return <PortfolioLMS />;
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Active training view
  if (activeTraining) {
    const videoId = getYouTubeVideoId(activeTraining.content_url);
    
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{activeTraining.title}</h1>
            <p className="text-muted-foreground">{activeTraining.category}</p>
          </div>
          <Button variant="outline" onClick={() => setActiveTraining(null)}>
            Exit Training
          </Button>
        </div>

        <Card>
          <CardContent className="p-6">
            {activeTraining.content_type === 'video' && activeTraining.content_url ? (
              <div className="aspect-video bg-black rounded-lg overflow-hidden">
                {videoId ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${videoId}?rel=0`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={activeTraining.title}
                  />
                ) : (
                  <video
                    src={activeTraining.content_url}
                    controls
                    className="w-full h-full"
                  />
                )}
              </div>
            ) : (
              <div className="prose max-w-none">
                <p>{activeTraining.content_text || activeTraining.description}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-between items-center">
          <div className="flex-1 mr-4">
            <Progress value={trainingProgress} className="h-2" />
            <p className="text-sm text-muted-foreground mt-1">{trainingProgress}% complete</p>
          </div>
          <Button onClick={handleCompleteTraining} className="btn-primary-glow">
            <CheckCircle className="w-4 h-4 mr-2" />
            Mark as Complete
          </Button>
        </div>
      </div>
    );
  }

  // Organization view
  return (
    <div data-testid="lms-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
            {selectedOrg ? `${selectedOrg.name} - Training` : 'Training Center'}
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage and track training modules for your organization
          </p>
        </div>
        {isSuperAdmin() && (
          <Button onClick={() => setShowModuleDialog(true)} className="btn-primary-glow">
            <Plus className="w-4 h-4 mr-2" />
            Create Training Module
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      {lmsStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Video className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{trainingModules.length}</p>
                  <p className="text-xs text-muted-foreground">Training Modules</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{lmsStats.total_completions || 0}</p>
                  <p className="text-xs text-muted-foreground">Completions</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{lmsStats.in_progress || 0}</p>
                  <p className="text-xs text-muted-foreground">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{lmsStats.avg_completion_rate || 0}%</p>
                  <p className="text-xs text-muted-foreground">Completion Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="modules" className="space-y-4">
        <TabsList>
          <TabsTrigger value="modules">Training Modules</TabsTrigger>
          {selectedOrg && <TabsTrigger value="progress">My Progress</TabsTrigger>}
          {selectedOrg && <TabsTrigger value="assigned">Assigned Training</TabsTrigger>}
        </TabsList>

        {/* Training Modules Tab */}
        <TabsContent value="modules" className="space-y-4">
          {trainingModules.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="p-12 text-center">
                <Video className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-semibold mb-2">No Training Modules</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first training module to get started
                </p>
                {isSuperAdmin() && (
                  <Button onClick={() => setShowModuleDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Training Module
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {trainingModules.map((module) => (
                <Card key={module.id} className="bg-card border-border hover:border-primary/50 transition-colors">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Video className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-base line-clamp-1">{module.title}</CardTitle>
                          <CardDescription className="text-xs">{module.category}</CardDescription>
                        </div>
                      </div>
                      <Badge variant={module.status === 'active' ? 'default' : 'secondary'}>
                        {module.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                      {module.description}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {module.duration_minutes} min
                      </span>
                      <span className="flex items-center gap-1">
                        <Target className="w-3 h-3" />
                        {module.passing_score}% to pass
                      </span>
                    </div>
                    <div className="flex gap-2">
                      {module.content_url && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStartTraining(module)}
                        >
                          <Play className="w-3 h-3 mr-1" />
                          Start
                        </Button>
                      )}
                      {isSuperAdmin() && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditModule(module)}
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedModule(module);
                              setShowAssignDialog(true);
                            }}
                          >
                            <Send className="w-3 h-3" />
                          </Button>
                          {module.status === 'draft' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePublishModule(module.id)}
                            >
                              <CheckCircle className="w-3 h-3" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-600"
                            onClick={() => handleDeleteModule(module.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* My Progress Tab */}
        <TabsContent value="progress" className="space-y-4">
          {myProgress.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="p-12 text-center">
                <GraduationCap className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-semibold mb-2">No Training Started</h3>
                <p className="text-muted-foreground">
                  Start a training module to track your progress
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {myProgress.map((progress) => (
                <Card key={progress.id} className="bg-card border-border">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          progress.status === 'completed' ? 'bg-green-500/10' : 'bg-amber-500/10'
                        }`}>
                          {progress.status === 'completed' ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <Clock className="w-5 h-5 text-amber-500" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{progress.module_title || 'Training Module'}</p>
                          <p className="text-sm text-muted-foreground">
                            {progress.status === 'completed' ? 'Completed' : `${progress.progress_percentage}% complete`}
                          </p>
                        </div>
                      </div>
                      {progress.status !== 'completed' && (
                        <Button size="sm" variant="outline">
                          Continue
                        </Button>
                      )}
                    </div>
                    {progress.status !== 'completed' && (
                      <Progress value={progress.progress_percentage} className="h-2 mt-3" />
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Assigned Training Tab */}
        <TabsContent value="assigned" className="space-y-4">
          {orgTrainingAssignments.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="p-12 text-center">
                <BookOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-semibold mb-2">No Assigned Training</h3>
                <p className="text-muted-foreground">
                  No training has been assigned to your organization yet
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {orgTrainingAssignments.map((assignment) => (
                <Card key={assignment.id} className="bg-card border-border">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Video className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{assignment.training_title || 'Training Module'}</p>
                          <p className="text-sm text-muted-foreground">
                            {assignment.due_date ? `Due: ${new Date(assignment.due_date).toLocaleDateString()}` : 'No due date'}
                          </p>
                        </div>
                      </div>
                      <Button size="sm">
                        <Play className="w-3 h-3 mr-1" />
                        Start
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Module Dialog */}
      <Dialog open={showModuleDialog} onOpenChange={handleModuleDialogClose}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingModule ? 'Edit Training Module' : 'Create Training Module'}
            </DialogTitle>
            <DialogDescription>
              {editingModule ? 'Update the training module details' : 'Add a new training module to the platform'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={editingModule ? handleUpdateModule : handleCreateModule} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={moduleForm.title}
                onChange={(e) => setModuleForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter module title"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={moduleForm.description}
                onChange={(e) => setModuleForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the training module"
                rows={3}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select
                  value={moduleForm.category}
                  onValueChange={(value) => setModuleForm(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {TRAINING_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="content_type">Content Type</Label>
                <Select
                  value={moduleForm.content_type}
                  onValueChange={(value) => setModuleForm(prev => ({ ...prev, content_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="document">Document</SelectItem>
                    <SelectItem value="interactive">Interactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="content_url">Video URL (YouTube or direct link)</Label>
              <div className="flex gap-2">
                <Input
                  id="content_url"
                  value={moduleForm.content_url}
                  onChange={(e) => setModuleForm(prev => ({ ...prev, content_url: e.target.value }))}
                  placeholder="https://youtube.com/watch?v=... or video URL"
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Supports YouTube links or direct video URLs
              </p>
            </div>

            <div className="space-y-2">
              <Label>Or Upload Video</Label>
              <div className="flex gap-2">
                <Input
                  type="file"
                  accept="video/*"
                  onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleVideoUpload}
                  disabled={!videoFile || uploadingVideo}
                >
                  {uploadingVideo ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="duration">Duration (minutes)</Label>
                <Input
                  id="duration"
                  type="number"
                  value={moduleForm.duration_minutes}
                  onChange={(e) => setModuleForm(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 0 }))}
                  min={0}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="passing_score">Passing Score (%)</Label>
                <Input
                  id="passing_score"
                  type="number"
                  value={moduleForm.passing_score}
                  onChange={(e) => setModuleForm(prev => ({ ...prev, passing_score: parseInt(e.target.value) || 80 }))}
                  min={0}
                  max={100}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleModuleDialogClose(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {editingModule ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  editingModule ? 'Update Module' : 'Create Module'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Training Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Training</DialogTitle>
            <DialogDescription>
              Assign &ldquo;{selectedModule?.title}&rdquo; to an organization
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAssignTraining} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="org">Organization *</Label>
              <Select
                value={assignForm.organization_id}
                onValueChange={(value) => setAssignForm(prev => ({ ...prev, organization_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select organization" />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map(org => (
                    <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="due_date">Due Date (optional)</Label>
              <Input
                id="due_date"
                type="date"
                value={assignForm.due_date}
                onChange={(e) => setAssignForm(prev => ({ ...prev, due_date: e.target.value }))}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAssignDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || !assignForm.organization_id}>
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Assign Training
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
